import {
  user,
  paymentMethods,
  recruitmentChannels,
  banks,
  locations,
  networks
} from '@/api/endpoints'
import {
  toggleFeaturesMap,
  recruitmentChannelMap,
  nameIdMap,
  nameMap,
  bankMap,
  locationMap,
  paymentMethodMap,
  paymentMethodClientTypeMap,
  availablePaymentMethodMap,
  consultantMap
} from './mapToClient'
import { paymentMethodMapToApi } from './mapToApi'
import { jforceTypes, languages, timeSlots } from '../../../api/endpoints'
import {eraseCookie, getCookie} from "../../../util/cookies";
import {getPreferredLanguageFromCookie, setPreferredLanguageFromCookie} from "@/util/locale";
import i18n from "@/i18n";
import store from "@/store";

const login = (code, email, referralCode) => {
  return new Promise(resolve => {
    user
      .login(code, email, referralCode)
      .then(response => {
        if (response.data.success) {
          // Customer information
          const customerData = response.data.response.customer_data


          let impersonatorEmail = getCookie("impersonate")
          if (impersonatorEmail) {
            store.commit('user/IS_IMPERSONATE', true)
            customerData.consultant.isImpersonate = true
          }

          // Check if the user is being impersonated
          console.log("User being impersonated(Login):", customerData.consultant.isImpersonate);

          // If the customer is a jforce agent
          // set a jforce token to be sent in the header of every request
          const isJForce = customerData.jforce_agent
          if (isJForce) {
            user.setJForceToken(response.data.response.jforce_token)
          }

          // Payment is mandatory when payment_method_mandatory is filled
          const isPaymentMethodMandatory = customerData.payment_method_mandatory !== null
          const hasPaymentMethod = customerData.has_payment_method

          // Payment Method is confirmed when:
          // - it is not mandatory
          // - or it is mandatory, the user comes from agreement and confirmed
          // - user comes directly to the app, already accepted the agreement and has a payment method
          const paymentMethodNeedConfirmation =
            isPaymentMethodMandatory &&
            (!customerData.accepted_terms_and_conditions || !hasPaymentMethod)

          const extraFlags = {
            isAuth: true,
            isJForce,
            isPaymentMethodMandatory, // used to know if the Payment Method menu is available in the app
            hasPaymentMethod, // used to know if the confirm-payment-method page must create or update
            paymentMethodNeedConfirmation, // used to know if the user must be redirected to the confirm-payment-method page
            mandatoryPaymentMethod: paymentMethodClientTypeMap(customerData.payment_method_mandatory),
            acceptedTermsAndCondition: customerData.accepted_terms_and_conditions,
            phoneNumberCode: customerData.consultant.country_code ? '+' + customerData.consultant.country_code : customerData.phone_country_code,
            toggleFeatures: customerData.feature_toggles ? toggleFeaturesMap(customerData.feature_toggles) : {},
            kyc: {
              content: customerData.updated_jforce_agreement_content,
              pdf: customerData.updated_jforce_agreement_pdf
            }
          }

          // Change locale if it is different from the current one in cookies
          const locale = getPreferredLanguageFromCookie()

          if (customerData.consultant.preferred_language && customerData.consultant.preferred_language !== locale) {
            setPreferredLanguageFromCookie(customerData.consultant.preferred_language)
            location.reload()
          }

          resolve({
            success: true,
            data: {
              ...consultantMap(customerData.consultant),
              ...extraFlags
            }
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch((error) => {
        console.log(error)
        resolve({
          success: false
        })
      })
  })
}

const acceptAgreement = consultantId => {
  return new Promise(resolve => {
    user
      .acceptAgreement({ consultantId })
      .then(response => {
        if (response.data.success) {
          resolve({
            success: true
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const buildUserErrors = error => {
  var errors = {}

  if (error.response) {
    var errorData = error.response.data
    if (errorData.error_message === 'VALIDATION_FAILED') {
      for (var index = 0; index < errorData.errors.length; index++) {
        if (errorData.errors[index].property === 'phone_number') {
          errors.phoneNumberError = true
        } else if (errorData.errors[index].property === 'full_name') {
          errors.fullNameError = true
        }
      }
    }
    if (errorData.error_message === 'UNKNOWN_ERROR') {
      errors.systemError = true
    }
  }

  return errors
}

const createUser = consultant => {
  return new Promise(resolve => {
    user
      .create({ data: consultant })
      .then(response => {
        if (response.data.success) {
          const consultant = response.data.response.consultant

          // set jforce token in order to be able to do requests to the api
          user.setJForceToken(response.data.response.jforce_token)

          // After create an account set isJForce flag to true
          // in order to advance on the flow
          resolve({
            success: true,
            data: { ...consultantMap(consultant), isJForce: true }
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(error => {
        resolve({
          success: false,
          apiError: true,
          errors: buildUserErrors(error)
        })
      })
  })
}

const updateUser = (consultantId, data) => {
  return new Promise(resolve => {
    user
      .update({ consultantId }, { data })
      .then(response => {
        if (response.data.success) {
          const consultant = response.data.response
          resolve({
            success: true,
            data: consultantMap(consultant)
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(error => {
        resolve({
          success: false,
          apiError: true,
          errors: buildUserErrors(error)
        })
      })
  })
}

const fetchAvailablePaymentMethods = () => {
  return new Promise(resolve => {
    paymentMethods
      .getAllAvailable()
      .then(response => {
        if (response.data.success) {
          var availablePaymentMethods = []
          const m = response.data.response.payment_methods
          for (var i = 0; i < m.length; i++) {
            availablePaymentMethods.push(availablePaymentMethodMap(m[i]))
          }

          resolve({
            success: true,
            data: availablePaymentMethods
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const fetchPaymentMethods = consultantId => {
  return new Promise(resolve => {
    paymentMethods
      .getAll({ consultantId })
      .then(response => {
        if (response.data.success) {
          const paymentMethods = response.data.response

          var list = {}
          for (var i = 0; i < paymentMethods.length; i++) {
            list[paymentMethods[i].id] = paymentMethodMap(paymentMethods[i])
          }

          resolve({
            success: true,
            data: list
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const buildPaymentMethodErrors = error => {
  var errors = {}

  if (error.response) {
    var errorData = error.response.data
    if (errorData.error_message === 'VALIDATION_FAILED') {
      for (var index = 0; index < errorData.errors.length; index++) {
        if (errorData.errors[index].property === 'phone_number') {
          errors.phoneNumberError = true
        } else if (errorData.errors[index].property === 'full_name') {
          errors.fullNameError = true
        }
      }
    }
  }

  return errors
}

const addPaymentMethod = (consultantId, paymentMethod) => {
  return new Promise(resolve => {
    paymentMethods
      .create(
        { consultantId, paymentType: paymentMethod.type },
        { data: paymentMethodMapToApi(paymentMethod) }
      )
      .then(response => {
        resolve({
          success: response.data.success,
          data: paymentMethodMap(response.data.response)
        })
      })
      .catch(error => {
        resolve({
          success: false,
          apiError: true,
          errors: buildPaymentMethodErrors(error)
        })
      })
  })
}

const getImageUrl = file => {
  return new Promise(resolve => {
    user
      .getImage(file)
      .then(response => {
        resolve({
          success: response.data.success,
          data: response.data.response
        })
      })
      .catch(error => {
        console.error(error)
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const updatePaymentMethod = (consultantId, paymentMethodId, paymentMethod) => {
  return new Promise(resolve => {
    const apiPaymentMethod = paymentMethodMapToApi(paymentMethod)
    paymentMethods
      .update(
        { consultantId, paymentType: paymentMethod.type, paymentMethodId },
        { data: apiPaymentMethod }
      )
      .then(response => {
        if (response.data.success) {
          resolve({
            success: true,
            data: paymentMethodMap(response.data.response)
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(error => {
        resolve({
          success: false,
          apiError: true,
          errors: buildPaymentMethodErrors(error)
        })
      })
  })
}

const fetchLocations = () => {
  return new Promise(resolve => {
    const params = {
      active: 1,
      sort: 'name'
    }
    locations
      .getAll({ params })
      .then(response => {
        if (response.data.success) {
          // map states
          var apiLocations = response.data.response.data
          var locations = []
          for (var i = 0; i < apiLocations.length; i++) {
            locations.push(locationMap(apiLocations[i]))
          }

          resolve({
            success: true,
            data: locations
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const fetchCities = stateId => {
  return new Promise(resolve => {
    const params = {
      active: 1,
      sort: 'name'
    }
    locations
      .getCities(stateId, { params })
      .then(response => {
        if (response.data.success) {
          // map states
          var apiLocations = response.data.response.data
          var locations = []
          for (var i = 0; i < apiLocations.length; i++) {
            locations.push(locationMap(apiLocations[i]))
          }

          resolve({
            success: true,
            data: locations
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const fetchBanks = () => {
  return new Promise(resolve => {
    const params = {
      active: 1,
      sort: 'name'
    }
    banks
      .getAll({ params })
      .then(response => {
        if (response.data.success) {
          // map banks
          var apiBanks = response.data.response.data
          var banks = []
          for (var i = 0; i < apiBanks.length; i++) {
            banks.push(bankMap(apiBanks[i]))
          }

          resolve({
            success: true,
            data: banks
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const fetchRecruitmentChannels = () => {
  return new Promise(resolve => {
    const params = {
      active: 1,
      sort: 'name'
    }

    recruitmentChannels
      .getAll({ params })
      .then(response => {
        if (response.data.success) {
          // map channels
          var apiChannels = response.data.response.data
          var channels = []
          for (var i = 0; i < apiChannels.length; i++) {
            channels.push(recruitmentChannelMap(apiChannels[i]))
          }

          resolve({
            success: true,
            data: channels
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const fetchNetworks = () => {
  return new Promise(resolve => {
    const params = {
      active: 1,
      sort: 'name'
    }

    networks
      .getAll({ params })
      .then(response => {
        if (response.data.success) {
          // map channels
          var networkList = response.data.response.data
          var networks = []
          for (var i = 0; i < networkList.length; i++) {
            networks.push(nameMap(networkList[i]).name)
          }

          resolve({
            success: true,
            data: networks
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const fetchJforceTypes = () => {
  return new Promise(resolve => {
    const params = {
      active: 1,
      sort: 'name'
    }

    jforceTypes
      .getAll({ params })
      .then(response => {
        if (response.data.success) {
          // map channels
          var typeList = response.data.response.data
          var jforcTypes = []
          for (var i = 0; i < typeList.length; i++) {
            jforcTypes.push(nameIdMap(typeList[i]))
          }

          resolve({
            success: true,
            data: jforcTypes
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}
const fetchLanguages = () => {
  return new Promise(resolve => {
    const params = {
      active: 1,
      sort: 'name'
    }

    languages
      .getAll({ params })
      .then(response => {
        if (response.data.success) {
          // map channels
          var langList = response.data.response.data
          var languages = []
          for (var i = 0; i < langList.length; i++) {
            languages.push(nameMap(langList[i]).name)
          }

          resolve({
            success: true,
            data: languages
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

const fetchSlots = () => {
  return new Promise(resolve => {
    const params = {
      active: 1,
      sort: 'sort'
    }

    timeSlots
      .getAll({ params })
      .then(response => {
        if (response.data.success) {
          // map channels
          var slotList = response.data.response.data
          var slots = []
          for (var i = 0; i < slotList.length; i++) {
            slots.push(nameIdMap(slotList[i]))
          }

          resolve({
            success: true,
            data: slots
          })
        } else {
          resolve({
            success: false
          })
        }
      })
      .catch(() => {
        resolve({
          success: false,
          apiError: true
        })
      })
  })
}

export default {
  login,
  getImageUrl,

  fetchLocations,
  fetchCities,
  fetchBanks,
  fetchRecruitmentChannels,
  fetchNetworks,
  fetchJforceTypes,
  fetchSlots,
  fetchLanguages,

  createUser,
  updateUser,

  acceptAgreement,

  fetchAvailablePaymentMethods,
  fetchPaymentMethods,

  addPaymentMethod,

  updatePaymentMethod,
}
